import { memo, useEffect, useState } from "react";
import { useTranslate } from "helpers";

import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { TextArea } from "ui/components/TextField/TextArea";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { CardSelect } from "ui/components/CardSelect";
import MultiDropdown from "ui/components/Dropdown/MultiDropdown/MultiDropdown";
import { MultiDropdownOption } from "ui/components/Dropdown/MultiDropdown/types";
import { getUsersAPI } from "services/api/users/api";
import { getOfficesAPI } from "services/api/offices/api";
import { initialPagination } from "ui/components/Table/consts";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";

// Postavke zrcale korak "Podaci oglasa" u wizardu, pa dijele iste primitive —
// tako se izgled ne može razići kad se čipovi ili sekcije promijene.
import { ChipToggle, SectionCard } from "ui/modules/sales/pages/listings/components/ListingEditCard/components/MarketplaceStep/primitives";

import { PAYMENT_OPTIONS, WARRANTY_OPTIONS } from "services/api/sales/listings/consts";
import { IMPORT_COST_OPTIONS } from "ui/modules/sales/pages/listings/components/ListingEditCard/consts";
import { updateListingDefaultsAPI } from "services/api/settings/listings/api";
import {
  ListingDefaultsFormValues,
  LocationDisplay,
  PriceIncludesImport,
} from "services/api/settings/listings/types";
import { ListingDefaultsEditCardProps } from "./types";
import { LOCATION_DISPLAY_OPTIONS } from "./consts";

const ListingDefaultsEditCard: React.FC<ListingDefaultsEditCardProps> = (props) => {
  const { show, formValues, setFormValues, onSuccess, onClose } = props;

  const { t } = useTranslate();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleText = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, ...({ [name]: value } as Partial<ListingDefaultsFormValues>) }));
  };

  // Šifrarnici za multi-odabir — djelatnici i poslovnice tenanta.
  const [userOptions, setUserOptions] = useState<MultiDropdownOption[]>([]);
  const [officeOptions, setOfficeOptions] = useState<MultiDropdownOption[]>([]);

  useEffect(() => {
    if (!show) return;

    const loadOptions = async () => {
      const pagination = { ...initialPagination, pageSize: 200 };

      const [users, offices] = await Promise.all([
        getUsersAPI(pagination).catch(() => null),
        getOfficesAPI(pagination).catch(() => null),
      ]);

      setUserOptions(
        (users?.data ?? []).map((user) => ({
          value: user.id ?? "",
          text: `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || user.userName || "",
        }))
      );
      setOfficeOptions(
        (offices?.data ?? []).map((office) => ({ value: office.id ?? "", text: office.name ?? "" }))
      );
    };

    loadOptions();
  }, [show]);

  const handleContactUsers = (_name: string, items: MultiDropdownOption[]) => {
    setFormValues((prev) => ({ ...prev, contactUserIds: items.map((item) => item.value) }));
  };

  const handleLocationOffices = (_name: string, items: MultiDropdownOption[]) => {
    setFormValues((prev) => ({ ...prev, locationOfficeIds: items.map((item) => item.value) }));
  };

  const handleLocationDisplay = (value: string) => {
    setFormValues((prev) => ({ ...prev, locationDisplay: value as LocationDisplay }));
  };

  const handlePriceIncludesImport = (value: string) => {
    setFormValues((prev) => ({ ...prev, priceIncludesImport: value as PriceIncludesImport }));
  };

  const handleWarranty = (_e: any, _name: string, item: { value: string }) => {
    setFormValues((prev) => ({ ...prev, warrantyCode: item.value }));
  };

  const handlePaymentToggle = (code: string) => {
    setFormValues((prev) => ({
      ...prev,
      paymentOptions: { ...prev.paymentOptions, [code]: !prev.paymentOptions[code] },
    }));
  };

  const toggle = (name: "extendedWarranty" | "g1ClubWarranty" | "videoCallViewing") => {
    setFormValues((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await updateListingDefaultsAPI(formValues, t);
      if (onSuccess) onSuccess();
    } catch {
      // greške rukuje createApiCall (snackbar)
    }
    setIsLoading(false);
  };

  return (
    <Modal show={show} onHide={onClose} size="medium">
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={t("admin.settings.listingdefaults.edit.title")}
        onHide={onClose}
      />
      {/* `fixedFooterSpacing` je za stare fiksne trake — ovdje footer stoji u modalu. */}
      <ModalBody fixedFooterSpacing={false}>
        <div className="p-4 flex flex-col gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">
              {t("admin.settings.listingdefaults.edit.title")}
            </h1>
            <p className="text-center text-md text-slate-600 mb-2 max-w-2xl mx-auto">
              Vrijednosti se primjenjuju na svaki novi oglas. Kontakt vrijedi za sve oglase, ostalo se
              po pojedinom oglasu može prilagoditi.
            </p>
          </div>

          <SectionCard
            title={t("admin.settings.listingdefaults.contact.title")}
            subtitle="Djelatnici čije se ime, brojevi i e-mail prikazuju kupcima."
          >
            <MultiDropdown
              name="contactUserIds"
              label={t("admin.settings.listingdefaults.contact.user")}
              placeholder="Odaberite djelatnike"
              options={userOptions}
              value={formValues.contactUserIds}
              onChange={handleContactUsers}
              width="full"
            />
          </SectionCard>

          <SectionCard
            title={t("admin.settings.listingdefaults.location.title")}
            subtitle={t("admin.settings.listingdefaults.location.description")}
          >
            {/* Lokacija oglasa čita se iz adrese poslovnice — ništa se ne upisuje ručno. */}
            <MultiDropdown
              name="locationOfficeIds"
              label="Poslovnice"
              placeholder="Odaberite poslovnice"
              options={officeOptions}
              value={formValues.locationOfficeIds}
              onChange={handleLocationOffices}
              width="full"
            />

            <div className="mt-4">
              <div className="text-sm font-medium text-slate-700 mb-2">
                {t("admin.settings.listingdefaults.location.display")}
              </div>
              <CardSelect
                options={LOCATION_DISPLAY_OPTIONS}
                value={formValues.locationDisplay}
                onChange={handleLocationDisplay}
              />
            </div>
          </SectionCard>

          <SectionCard
            title={t("admin.settings.listingdefaults.payment.title")}
            subtitle={t("admin.settings.listingdefaults.payment.description")}
          >

            <div className="flex flex-wrap gap-2 mb-4">
              {PAYMENT_OPTIONS.map((opt) => (
                <ChipToggle
                  key={opt.code}
                  label={opt.label}
                  selected={!!formValues.paymentOptions[opt.code]}
                  onToggle={() => handlePaymentToggle(opt.code)}
                />
              ))}
            </div>
            <div className="mb-4">
              <div className="text-sm font-medium text-slate-700 mb-2">
                {t("admin.settings.listingdefaults.payment.import")}
              </div>
              <CardSelect
                options={IMPORT_COST_OPTIONS}
                value={formValues.priceIncludesImport}
                onChange={handlePriceIncludesImport}
              />
            </div>

          </SectionCard>

          <SectionCard
            title={t("admin.settings.listingdefaults.warranty.title")}
            subtitle={t("admin.settings.listingdefaults.warranty.description")}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <Dropdown
                  name="warrantyCode"
                  label={t("admin.settings.listingdefaults.warranty.warranty")}
                  value={formValues.warrantyCode}
                  options={WARRANTY_OPTIONS}
                  onChange={handleWarranty}
                />
              </div>

              {/* `pt-8` poravnava čip s poljem dropdowna, a ne s labelom iznad njega. */}
              <div className="flex items-center sm:pt-8">
                <ChipToggle
                  label={t("admin.settings.listingdefaults.warranty.extended")}
                  selected={formValues.extendedWarranty}
                  onToggle={() => toggle("extendedWarranty")}
                />
              </div>

              <div className="flex items-center sm:pt-8">
                <ChipToggle
                  label={t("admin.settings.listingdefaults.warranty.g1club")}
                  selected={formValues.g1ClubWarranty}
                  onToggle={() => toggle("g1ClubWarranty")}
                />
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title={t("admin.settings.listingdefaults.other.title")}
            subtitle={t("admin.settings.listingdefaults.other.description")}
          >
            <ChipToggle
              label={t("admin.settings.listingdefaults.other.videocall")}
              selected={formValues.videoCallViewing}
              onToggle={() => toggle("videoCallViewing")}
            />
          </SectionCard>
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="flex justify-between gap-3 px-4 py-3">
          <Button variant="outlined" children={t("admin.close")} onClick={onClose} disabled={isLoading} />
          <Button variant="primary" children={t("admin.save")} onClick={handleSave} loading={isLoading} />
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default memo(ListingDefaultsEditCard);
